import { Alert, Card, Grid, Icon, Tooltip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDDialog from "components/MDDialog";
import MDTypography from "components/MDTypography";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { useEffect, useState } from "react";
import Service from "services/empresas";
import { ptBR_DataGrid } from "utils/datagrid";
import FormEmpresa, { TIPO_EMPRESA } from "./components/formEmpresa";

const formatarCnpj = (v = "") =>
  v.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");

// Empresas do grupo (as Primes): matriz e filiais. Base para o filtro por
// empresa no financeiro e para o multi-tenant futuro.
export default function Empresas() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState({ page: 0, pageSize: 50 });
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null); // null fechado | "nova" | id
  const [remover, setRemover] = useState(null);
  const [erro, setErro] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data, total: t } = await Service.paginate(
        pagina.page,
        pagina.pageSize,
        "&sortBy=razaoSocial&sortOrder=1"
      );
      setTotal(t);
      setRows(
        data.map((e) => ({
          id: e._id,
          action: e,
          razaoSocial: e.razaoSocial,
          nomeFantasia: e.nomeFantasia,
          cnpj: formatarCnpj(e.cnpj),
          tipo:
            e.tipo === TIPO_EMPRESA.FILIAL
              ? `Filial${
                  e.matriz
                    ? ` de ${e.matriz.nomeFantasia || e.matriz.razaoSocial}`
                    : ""
                }`
              : "Matriz",
          uf: e.uf,
          ativo: e.ativo === false ? "Inativa" : "Ativa",
        }))
      );
    } catch (e) {
      setErro(e?.error || "Erro ao carregar as empresas.");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [pagina]);

  const confirmarRemocao = async () => {
    setErro("");
    try {
      await Service.delete(remover._id);
    } catch (e) {
      setErro(e?.error || "Erro ao excluir a empresa.");
    }
    setRemover(null);
    fetchData();
  };

  const acoes = ({ row }) => (
    <MDBox display="flex" gap={1}>
      <Tooltip title="Editar">
        <MDButton
          variant="gradient"
          color="info"
          iconOnly
          onClick={() => setForm(row.action._id)}
        >
          <Icon fontSize="small">edit</Icon>
        </MDButton>
      </Tooltip>
      <Tooltip title="Remover">
        <MDButton
          variant="gradient"
          color="error"
          iconOnly
          onClick={() => setRemover(row.action)}
        >
          <Icon fontSize="small">delete_forever</Icon>
        </MDButton>
      </Tooltip>
    </MDBox>
  );

  return (
    <DashboardLayout>
      <MDBox pt={3} pb={3}>
        {form && (
          <FormEmpresa
            currentIdAction={form === "nova" ? null : form}
            onClose={() => setForm(null)}
            fetchData={fetchData}
          />
        )}
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <MDTypography variant="h6" color="white" mt={1}>
                  Empresas
                </MDTypography>
                <MDButton
                  variant="contained"
                  type="button"
                  onClick={() => setForm("nova")}
                >
                  Adicionar
                </MDButton>
              </MDBox>
              <MDBox pt={3}>
                <Card sx={{ padding: 2 }}>
                  {erro && (
                    <Alert
                      severity="error"
                      sx={{ mb: 2 }}
                      onClose={() => setErro("")}
                    >
                      {erro}
                    </Alert>
                  )}
                  <DataGrid
                    rows={rows}
                    columns={[
                      {
                        headerName: "Razão social",
                        field: "razaoSocial",
                        flex: 0.3,
                      },
                      {
                        headerName: "Nome fantasia",
                        field: "nomeFantasia",
                        flex: 0.2,
                      },
                      { headerName: "CNPJ", field: "cnpj", flex: 0.18 },
                      { headerName: "Tipo", field: "tipo", flex: 0.15 },
                      { headerName: "UF", field: "uf", flex: 0.05 },
                      { headerName: "Situação", field: "ativo", flex: 0.08 },
                      {
                        headerName: "Ações",
                        field: "action",
                        flex: 0.12,
                        renderCell: acoes,
                        sortable: false,
                      },
                    ]}
                    rowCount={total}
                    paginationMode="server"
                    paginationModel={pagina}
                    onPaginationModelChange={setPagina}
                    pageSizeOptions={[50, 100]}
                    disableColumnFilter
                    rowSelection={false}
                    loading={loading}
                    localeText={ptBR_DataGrid}
                    sx={{
                      ".MuiDataGrid-virtualScroller": { minHeight: "100px" },
                    }}
                  />
                </Card>
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        <MDDialog
          open={Boolean(remover)}
          handleClose={() => setRemover(null)}
          title={"Tem certeza que deseja remover?"}
          actions={
            <>
              <MDButton
                variant="gradient"
                color="info"
                fullWidth
                onClick={confirmarRemocao}
              >
                Confirmar
              </MDButton>
              <MDButton
                variant="gradient"
                color="error"
                fullWidth
                onClick={() => setRemover(null)}
              >
                Cancelar
              </MDButton>
            </>
          }
        >
          {remover?.razaoSocial}
        </MDDialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
